/**
 * Recordings Service
 * API client for session recording operations
 */

import { apiClient } from './api.client';
import type {
  SessionRecording,
  RecordingListFilters,
  UpdateRecordingRequest,
} from '../types/api.types';

export interface RecordingsListResponse {
  recordings: SessionRecording[];
  total: number;
  limit: number;
  offset: number;
}

export interface RecordingDetailsResponse {
  success: boolean;
  recording: SessionRecording;
}

export interface DownloadUrlResponse {
  url: string;
  expiresAt: string;
}

/**
 * Recordings Service
 * Handles all API calls for session recordings
 */
export const recordingsService = {
  /**
   * List recordings accessible to user
   * @param filters - Optional filters for channelId, dmThreadId, dates, status
   * @returns List of recordings with pagination
   */
  list: async (filters?: RecordingListFilters): Promise<RecordingsListResponse> => {
    const response = await apiClient.get<RecordingsListResponse>('/recordings', {
      params: filters,
    });
    return response.data;
  },

  /**
   * Get detailed recording information
   * @param id - Recording ID
   * @returns Full recording details
   */
  get: async (id: string): Promise<SessionRecording> => {
    const response = await apiClient.get<RecordingDetailsResponse>(`/recordings/${id}`);
    return response.data.recording;
  },

  /**
   * Get presigned S3 URL for downloading recording
   * @param id - Recording ID
   * @returns Presigned URL and expiration
   */
  getDownloadUrl: async (id: string): Promise<DownloadUrlResponse> => {
    const response = await apiClient.get<DownloadUrlResponse>(`/recordings/${id}/download`);
    return response.data;
  },

  /**
   * Update recording metadata (title, description)
   * @param id - Recording ID
   * @param data - Updates (title, description)
   * @returns Updated recording
   */
  update: async (id: string, data: UpdateRecordingRequest): Promise<SessionRecording> => {
    const response = await apiClient.patch<RecordingDetailsResponse>(`/recordings/${id}`, data);
    return response.data.recording;
  },

  /**
   * Delete recording (admin only)
   * @param id - Recording ID
   * @returns Success message
   */
  delete: async (id: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.delete<{ success: boolean; message: string }>(`/recordings/${id}`);
    return response.data;
  },
};
